import type { ReportFilter } from './reports.types.ts';

export type ReportFieldType = 'text' | 'number' | 'date' | 'select' | 'multiSelect' | 'checkbox';
export type ReportLookup = 'products' | 'vendors' | 'psls' | 'productTypes' | 'vqcaLevels' | 'categoryManagers';

export interface ReportFieldOption {
  label: string;
  value: string | number;
}

export interface ReportFieldConfig {
  name: keyof ReportFilter;
  label: string;
  type: ReportFieldType;
  required?: boolean;
  lookup?: ReportLookup;
  options?: ReportFieldOption[];
  width?: number;
}

export interface LegacyReportConfig {
  id: string;
  title: string;
  description: string;
  fields: ReportFieldConfig[];
  automaticRun?: boolean;
  route?: string;
}

const experimentStatuses: ReportFieldOption[] = [
  { value: 4, label: 'PASSED' },
  { value: 5, label: 'UNDER REVIEW' },
  { value: 6, label: 'CONDITIONALLY APPROVED' },
  { value: 7, label: 'FAILED' },
];

const coaStatuses: ReportFieldOption[] = [
  { value: '1', label: 'CREATED' },
  { value: '2', label: 'UNDER REVIEW' },
  { value: '3', label: 'PASSED' },
  { value: '4', label: 'FAILED' },
  { value: '5', label: 'CONDITIONALLY APPROVED' },
];

const sampleStatuses: ReportFieldOption[] = [
  { value: '1', label: 'PASSED' },
  { value: '2', label: 'IN PROCESS' },
  { value: '3', label: 'FAILED' },
  { value: '8', label: 'RE-OPEN' },
  { value: '9', label: 'LOGGED IN' },
];

const vqcaStatuses: ReportFieldOption[] = [
  { value: '1', label: 'STARTED' },
  { value: '2', label: 'SIGNED BY GLOBAL QUALITY LEAD' },
  { value: '3', label: 'SIGNED BY CATEGORY MANAGER' },
  { value: '4', label: 'SIGNED BY VENDOR' },
  { value: '5', label: 'APPROVED' },
  { value: '6', label: 'REJECTED' },
  { value: '7', label: 'REJECTED BY CATEGORY MANAGER' },
  { value: '8', label: 'INACTIVE' },
];

const requestStatuses: ReportFieldOption[] = [
  { value: '1', label: 'CREATED' },
  { value: '2', label: 'APPROVED BY CATEGORY MANAGER' },
  { value: '3', label: 'REJECTED BY CATEGORY MANAGER' },
  { value: '4', label: 'APPROVED BY GLOBAL QUALITY LEAD' },
  { value: '5', label: 'REJECTED BY GLOBAL QUALITY LEAD' },
  { value: '6', label: 'VQCA GENERATED' },
  { value: '7', label: 'SYSTEM GENERATED' },
  { value: '8', label: 'SAVED' },
];

const datesRequired: ReportFieldConfig[] = [
  { name: 'fromDate', label: 'From', type: 'date', required: true },
  { name: 'toDate', label: 'To', type: 'date', required: true },
];

const experimentBase: ReportFieldConfig[] = [
  { name: 'productName', label: 'Product', type: 'select', lookup: 'products' },
  { name: 'vendorId', label: 'Vendor', type: 'select', lookup: 'vendors' },
  { name: 'fromDate', label: 'From Date', type: 'date' },
  { name: 'toDate', label: 'To Date', type: 'date' },
];

export const legacyReportConfigs: LegacyReportConfig[] = [
  {
    id: 'complete-ship-vs-coa',
    title: 'Complete Ship vs. COA',
    description: 'Completed SAP shipments matched to certificates of analysis.',
    fields: [
      ...datesRequired,
      { name: 'vendorName', label: 'Vendor Name', type: 'select', lookup: 'vendors' },
      { name: 'productName', label: 'Product Name', type: 'select', lookup: 'products' },
    ],
  },
  {
    id: 'missing-coa',
    title: "Missing COA's",
    description: 'Released SAP shipments without a matching certificate of analysis.',
    fields: datesRequired,
  },
  {
    id: 'qa-lab-experiment-results',
    title: 'QA Lab Experiment Results',
    description: 'QA sample experiment results by product, vendor, date, QA number, and status.',
    fields: [
      ...experimentBase,
      { name: 'qaNumber', label: 'QA Number', type: 'number' },
      { name: 'statuses', label: 'Status', type: 'multiSelect', options: experimentStatuses },
    ],
  },
  {
    id: 'coa-lab-experiment-results',
    title: 'COA Lab Experiment Results',
    description: 'COA experiment results by product, vendor, and date.',
    fields: experimentBase,
  },
  {
    id: 'coa-performance-specification-results',
    title: 'COA PerformanceSpecification Results',
    description: 'Pivoted COA performance specification results by lot.',
    fields: experimentBase,
  },
  {
    id: 'qa-performance-specification-results',
    title: 'QA PerformanceSpecification Results',
    description: 'Pivoted QA performance specification results by sample.',
    fields: experimentBase,
  },
  {
    id: 'manage-qa',
    title: 'Manage QA',
    description: 'QA samples with legacy search, paging, sorting, detail navigation, and export.',
    fields: [],
  },
  {
    id: 'missing-po-summary',
    title: 'Missing PO Summary',
    description: 'Monthly missing purchase-order summary.',
    fields: datesRequired,
  },
  {
    id: 'coa',
    title: 'COA',
    description: 'Complete certificate-of-analysis search report.',
    fields: [
      { name: 'fromDate', label: 'Issue Date From', type: 'date' },
      { name: 'toDate', label: 'Issue Date To', type: 'date' },
      { name: 'productionFromDate', label: 'Production Date From', type: 'date' },
      { name: 'productionToDate', label: 'Production Date To', type: 'date' },
      { name: 'expirationFromDate', label: 'Expiry Date From', type: 'date' },
      { name: 'expirationToDate', label: 'Expiry Date To', type: 'date' },
      { name: 'productName', label: 'Product Name', type: 'select', lookup: 'products' },
      { name: 'vendorName', label: 'Vendor Name', type: 'select', lookup: 'vendors' },
      { name: 'statuses', label: 'Status', type: 'select', options: coaStatuses },
      { name: 'lotNumber', label: 'Lot Number', type: 'text' },
      { name: 'packageLotNumber', label: 'Package Lot Number', type: 'text' },
    ],
  },
  {
    id: 'non-compliance-coas',
    title: "Non-Compliance COA's",
    description: 'Failed COAs with the legacy operational search filters.',
    automaticRun: true,
    fields: [
      { name: 'lotNumber', label: 'Lot Number', type: 'text' },
      { name: 'vendorName', label: 'Vendor', type: 'select', lookup: 'vendors' },
      { name: 'poNumber', label: 'PO Number', type: 'text' },
      { name: 'productName', label: 'Product Name', type: 'select', lookup: 'products' },
      { name: 'fromDate', label: 'Issue Date From', type: 'date' },
      { name: 'toDate', label: 'Issue Date To', type: 'date' },
      { name: 'productionFromDate', label: 'Production Date From', type: 'date' },
      { name: 'productionToDate', label: 'Production Date To', type: 'date' },
      { name: 'productTypeId', label: 'Product Type', type: 'select', lookup: 'productTypes' },
    ],
  },
  {
    id: 'stp-vs-product',
    title: 'STP vs. Product',
    description: 'Standard test procedures assigned to products.',
    fields: [
      { name: 'stpNumber', label: 'STP Number', type: 'text' },
      { name: 'stpName', label: 'STP Name', type: 'text' },
      { name: 'productName', label: 'Product Name', type: 'select', lookup: 'products' },
    ],
  },
  {
    id: 'vqca',
    title: 'VQCA',
    description: 'VQCA and VQCA request status report.',
    fields: [
      { name: 'vqcaTypes', label: 'Type', type: 'multiSelect', options: [{ value: 1, label: 'VQCA' }, { value: 2, label: 'VQCA Request' }] },
      { name: 'vendorName', label: 'Vendor', type: 'select', lookup: 'vendors' },
      { name: 'productName', label: 'Product Type', type: 'select', lookup: 'products' },
      { name: 'categoryManager', label: 'Category Manager', type: 'select', lookup: 'categoryManagers' },
      { name: 'vqcaLevel', label: 'VQCA Level', type: 'select', lookup: 'vqcaLevels' },
      { name: 'vqcaStatus', label: 'VQCA Status', type: 'select', options: vqcaStatuses },
      { name: 'requestStatus', label: 'Request Status', type: 'select', options: requestStatuses },
    ],
  },
  {
    id: 'qalab',
    title: 'QALab',
    description: 'QA Lab sample report.',
    fields: [
      { name: 'fromDate', label: 'Logged Date From', type: 'date' },
      { name: 'toDate', label: 'Logged Date To', type: 'date' },
      { name: 'statuses', label: 'Status', type: 'select', options: sampleStatuses },
      { name: 'qaNumber', label: 'QA Number', type: 'number' },
      { name: 'lotNumber', label: 'Lot Number', type: 'text' },
      { name: 'productName', label: 'Product', type: 'select', lookup: 'products' },
    ],
  },
  {
    id: 'unlisted-material-exception',
    title: 'Unlisted Material# Exception Report',
    description: 'SAP material numbers that are not mapped in QCDOC.',
    fields: [],
    automaticRun: true,
  },
  {
    id: 'missing-po-exception',
    title: 'Missing PO # Exception Report',
    description: 'SAP purchase orders missing required QCDOC associations.',
    fields: [],
    automaticRun: true,
  },
  {
    id: 'unlisted-vendor-exception',
    title: 'Unlisted Vendor Exception Report',
    description: 'SAP vendors that are not listed in QCDOC.',
    fields: [],
    automaticRun: true,
  },
  {
    id: 'qa-workload',
    title: 'QA WorkLoad Report',
    description: 'QA workload by sample receiving date and completion state.',
    fields: [
      { name: 'completedReports', label: 'Completed Reports', type: 'checkbox' },
      { name: 'fromDate', label: 'Sample Receiving Date From', type: 'date' },
      { name: 'toDate', label: 'Sample Receiving Date To', type: 'date' },
    ],
  },
  {
    id: 'metrics',
    title: 'Metrics Report',
    description: 'PSL-scoped quality metrics for the selected date range.',
    fields: [
      { name: 'pslIds', label: 'PSL', type: 'multiSelect', lookup: 'psls', required: true },
      { name: 'fromDate', label: 'Date From', type: 'date' },
      { name: 'toDate', label: 'Date To', type: 'date' },
    ],
  },
  {
    id: 'sap-po-log',
    title: 'SAP PO Log Report',
    description: 'SAP PO processing log by user, PO, material, and date.',
    fields: [
      { name: 'userId', label: 'User ID', type: 'text' },
      { name: 'poNumber', label: 'PO Number', type: 'text' },
      { name: 'materialNumber', label: 'Material Number', type: 'text' },
      { name: 'fromDate', label: 'Date From', type: 'date' },
      { name: 'toDate', label: 'Date To', type: 'date' },
    ],
  },
];

export const legacyReportById = Object.fromEntries(
  legacyReportConfigs.map((report) => [report.id, report]),
) as Record<string, LegacyReportConfig>;